// src/pages/referrals/DoctorReferralCreate.jsx //
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import supabase from "@/services/supabaseClient";

import LayoutShell from "@/components/ui/LayoutShell.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import ReferralFormCard from "@/components/referrals/ReferralFormCard.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { resolveValidatedSession } from "@/lib/authSession.js";
import { useLanguage } from "@/contexts/LanguageContext.jsx";

export default function DoctorReferralCreate() {
  const nav = useNavigate();
  const { appointmentId } = useParams();
  const { loading: authLoading, user } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = React.useState(true);
  const [patientUserId, setPatientUserId] = React.useState(null);
  const [doctorUserId, setDoctorUserId] = React.useState(null);

  React.useEffect(() => {
    let alive = true;

    async function loadAppointmentContext() {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        let session = sessionData?.session || null;
        if (!session) {
          session = await resolveValidatedSession({ signOutOnFailure: false });
        }

        if (!alive) return;
        if (!session?.access_token) {
          setPatientUserId(null);
          setDoctorUserId(null);
          return;
        }

        const { data, error } = await supabase.rpc(
          "afrimed_clinician_appointment_patient_identity",
          { p_appointment_ids: [appointmentId] },
        );
        if (error) throw error;

        if (!alive) return;
        // Resolve the subject from the appointment-bound server projection on
        // every entry, including a direct URL or refresh. The referral RPC
        // independently rechecks this exact appointment/Patient pair.
        const identity = (Array.isArray(data) ? data : []).find(
          (row) => row?.appointment_id === appointmentId,
        );
        setPatientUserId(identity?.patient_user_id || null);
        setDoctorUserId(user?.id || null);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.info("[ReferralPackagePreparation]", {
            event: "appointment_context_failed",
            appointmentId,
            code: error?.code || null,
            message: error?.message || "unknown_error",
          });
        }
        if (!alive) return;
        setPatientUserId(null);
        setDoctorUserId(null);
      } finally {
        if (alive) setLoading(false);
      }
    }

    if (authLoading) return () => {
      alive = false;
    };

    loadAppointmentContext();

    return () => {
      alive = false;
    };
  }, [appointmentId, authLoading, user?.id]);

  if (!loading && (!patientUserId || !doctorUserId)) {
    return (
      <LayoutShell hideTabs flushTop freeScroll railClassName="w-full">
        <TopBarCenter
          title={t("referrals.doctor.title", "Refer patient")}
          subtitle={t("referrals.doctor.subtitle", "Send patient to specialist care")}
          onBack={() => nav(-1)}
        />

        <div className="w-full min-w-0 max-w-full pt-4 pb-24">
          <div className="w-full space-y-5">
            <div className="text-body-medium text-on-surface-var">
              {t("referrals.doctor.missingContext", "Missing appointment context.")}
            </div>
          </div>
        </div>
      </LayoutShell>
    );
  }

  return (
    <LayoutShell hideTabs flushTop freeScroll railClassName="w-full">
      <TopBarCenter
        title={t("referrals.doctor.title", "Refer patient")}
        subtitle={t("referrals.doctor.subtitle", "Send patient to specialist care")}
        onBack={() => nav(-1)}
      />

      <div className="w-full min-w-0 max-w-full pt-4 pb-24">
        <div className="space-y-5">
          {loading ? (
            <div className="text-body-medium text-on-surface-var">
              {t("referrals.doctor.loading", "Loading referral form…")}
            </div>
          ) : (
            <ReferralFormCard
              appointmentId={appointmentId}
              patientUserId={patientUserId}
              doctorUserId={doctorUserId}
              onSuccess={() => {}}
            />
          )}
        </div>
      </div>
    </LayoutShell>
  );
}
